import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sliders, Globe, Eye, EyeOff, Save, RefreshCw,
  Star, Shield, Mic, Volume2, Languages, Accessibility,
  CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  Power, Database, AlertTriangle, Send, Mail, Sparkles,
  Lock, KeyRound, Clock as ClockIcon
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RESTAURANT_ID = "default"; // Will be dynamic per-tenant

// ── Helpers ──────────────────────────────────────────────────────────

const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

// ── Sub-components ────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title, subtitle, color = "text-[#D4AF37]" }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="p-2 rounded-lg bg-white/5 border border-white/10">
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <div>
      <h3 className="font-semibold text-white text-sm">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const PersonaSlider = ({ label, description, value, min, max, step = 1, format, onChange, color }) => {
  const pct = ((value - min) / (max - min)) * 100;
  const displayVal = format ? format(value) : value;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium text-white">{label}</span>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
        <span
          className="text-sm font-mono font-bold px-3 py-1 rounded-full border"
          style={{ color, borderColor: color + "44", background: color + "11" }}
        >
          {displayVal}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-white/10">
        <div
          className="absolute left-0 top-0 h-full rounded-full transition-all duration-150"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}88, ${color})` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-lg transition-all duration-150"
          style={{ left: `calc(${pct}% - 8px)`, background: color }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-600">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
};

const Toggle = ({ label, description, checked, onChange, accent = "#D4AF37" }) => (
  <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
    <div>
      <p className="text-sm font-medium text-white">{label}</p>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <button
      data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={() => onChange(!checked)}
      className="relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0"
      style={{ background: checked ? accent : "rgba(255,255,255,0.1)" }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300"
        style={{ left: checked ? "calc(100% - 22px)" : "2px" }}
      />
    </button>
  </div>
);

const EchoPreview = ({ prompt, loading }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-[#00FF41]/20 bg-[#00FF41]/03 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="relative flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full rounded-full bg-[#00FF41] ${loading ? "animate-ping" : ""} opacity-75`} />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF41]" />
          </div>
          <span className="text-sm font-mono text-[#00FF41]">
            {loading ? "Generating Echo identity..." : "Echo Identity Preview"}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <div className="rounded-lg bg-black/40 border border-white/5 p-4 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Updating preview...</span>
              </div>
            ) : prompt ? (
              <pre className="text-xs text-gray-400 whitespace-pre-wrap font-mono leading-relaxed">
                {prompt}
              </pre>
            ) : (
              <p className="text-gray-600 text-xs">Save your config to preview Echo's identity.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Founding Badge ──────────────────────────────────────────────────

export const FoundingBadge = () => (
  <div
    data-testid="founding-member-badge"
    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
    style={{
      background: "linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))",
      border: "1px solid rgba(212,175,55,0.4)",
      color: "#D4AF37",
      boxShadow: "0 0 12px rgba(212,175,55,0.2)",
    }}
  >
    <Star className="w-3 h-3 fill-current" />
    Founding Member
  </div>
);

// ── Confetti Burst ───────────────────────────────────────────────────

function ConfettiBurst() {
  const COLORS = ["#D4AF37", "#00FF41", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899"];
  const pieces = Array.from({ length: 28 }, (_, i) => ({
    id: i,
    color: COLORS[i % COLORS.length],
    x: (Math.random() - 0.5) * 360,
    y: -(Math.random() * 220 + 80),
    rotate: Math.random() * 720 - 360,
    size: Math.random() * 8 + 5,
  }));
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden" style={{ zIndex: 50 }}>
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-sm"
          style={{ width: p.size, height: p.size * 0.6, background: p.color }}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
          animate={{ x: p.x, y: p.y, opacity: 0, rotate: p.rotate, scale: 0.4 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        />
      ))}
    </div>
  );
}

// ── Founding Welcome Panel ───────────────────────────────────────────

function FoundingWelcomePanel({ token, config }) {
  const profile = config?.restaurant_profile || {};

  const [founderName,    setFounderName]    = useState("");
  const [email,          setEmail]          = useState("");
  const [restaurantName, setRestaurantName] = useState(profile.name || "Argus Xenia");
  const [ccUrl,          setCcUrl]          = useState("https://argusxenia.com/admin/command-center");
  const [sending,        setSending]        = useState(false);
  const [status,         setStatus]         = useState(null);
  const [errorMsg,       setErrorMsg]       = useState("");
  const [showConfetti,   setShowConfetti]   = useState(false);

  useEffect(() => { if (profile.name) setRestaurantName(profile.name); }, [profile.name]);

  const handleSend = async () => {
    if (!email || !founderName) return;
    setSending(true);
    setStatus(null);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/api/admin/send-founding-welcome`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ to_email: email, founder_name: founderName, restaurant_name: restaurantName, command_centre_url: ccUrl }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus("success");
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 1400);
        setEmail("");
        setFounderName("");
      } else {
        setStatus("error");
        setErrorMsg(data.detail || "Send failed — check Resend API key");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error — try again");
    } finally {
      setSending(false);
    }
  };

  const inputStyle = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" };

  return (
    <div className="rounded-2xl p-6 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))", border: "1px solid rgba(212,175,55,0.25)" }}>
      {showConfetti && <ConfettiBurst />}

      <SectionHeader icon={Mail} title="Send Founding Welcome"
        subtitle="One click sends the branded ✦ Welcome email to a new Founding Member." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {[
          { label: "Founder Name", placeholder: "e.g. Andreas", value: founderName, onChange: setFounderName, testid: "welcome-founder-name", badge: null },
          { label: "Founder Email", placeholder: "e.g. chef@restaurant.com", value: email, onChange: setEmail, testid: "welcome-email", badge: null },
          { label: "Restaurant Name", placeholder: "Restaurant", value: restaurantName, onChange: setRestaurantName, testid: "welcome-restaurant-name", badge: "auto-filled" },
          { label: "Command Centre URL", placeholder: "https://...", value: ccUrl, onChange: setCcUrl, testid: "welcome-cc-url", badge: null },
        ].map(({ label, placeholder, value, onChange, testid, badge }) => (
          <div key={testid}>
            <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">
              {label}{badge && <span className="text-[#D4AF37] ml-1 normal-case">{badge}</span>}
            </label>
            <input data-testid={testid} type="text" value={value} placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none transition-colors"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "rgba(212,175,55,0.5)")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")} />
          </div>
        ))}
      </div>

      <AnimatePresence>
        {status === "success" && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex items-center gap-2 mb-4 px-4 py-3 rounded-xl text-sm"
            style={{ background: "rgba(0,255,65,0.08)", border: "1px solid rgba(0,255,65,0.25)", color: "#00FF41" }}>
            <CheckCircle className="w-4 h-4" />
            Welcome email sent! The founder will receive their Argus Xenia access in seconds.
          </motion.div>
        )}
        {status === "error" && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="flex items-center gap-2 mb-4 px-4 py-3 rounded-xl text-sm"
            style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}>
            <AlertCircle className="w-4 h-4" />
            {errorMsg}
          </motion.div>
        )}
      </AnimatePresence>

      <button data-testid="send-founding-welcome-btn" onClick={handleSend}
        disabled={sending || !email || !founderName}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all"
        style={{
          background: email && founderName ? "linear-gradient(135deg, #D4AF37, #b8941f)" : "rgba(255,255,255,0.05)",
          color: email && founderName ? "#000" : "#4b5563",
          cursor: email && founderName && !sending ? "pointer" : "not-allowed",
          boxShadow: email && founderName ? "0 4px 20px rgba(212,175,55,0.3)" : "none",
        }}>
        {sending ? <RefreshCw className="w-4 h-4 animate-spin" />
          : status === "success" ? <><Sparkles className="w-4 h-4" /> Sent — Send Another</>
          : <><Send className="w-4 h-4" /> Send Founding Welcome ✦</>}
      </button>
    </div>
  );
}

// ── Security Panel ───────────────────────────────────────────────────

function SecurityPanel({ token }) {
  const [secInfo,    setSecInfo]    = useState(null);
  const [current,   setCurrent]    = useState("");
  const [newPwd,    setNewPwd]     = useState("");
  const [confirm,   setConfirm]    = useState("");
  const [showCur,   setShowCur]    = useState(false);
  const [showNew,   setShowNew]    = useState(false);
  const [saving,    setSaving]     = useState(false);
  const [status,    setStatus]     = useState(null); // "success"|"error"
  const [errMsg,    setErrMsg]     = useState("");

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/admin/security-info`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setSecInfo(d))
      .catch(() => {});
  }, [token]);

  const pwdValid = current && newPwd.length >= 8 && newPwd === confirm;

  const handleChangePassword = async () => {
    setSaving(true); setStatus(null); setErrMsg("");
    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: current, new_password: newPwd }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus("success");
        setCurrent(""); setNewPwd(""); setConfirm("");
        // Refresh security info
        fetch(`${API_URL}/api/admin/security-info`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => setSecInfo(d)).catch(() => {});
      } else {
        setStatus("error");
        setErrMsg(data.detail || "Password change failed.");
      }
    } catch {
      setStatus("error"); setErrMsg("Network error — try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2.5 pr-10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none transition-colors";
  const inputStyle = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" };

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/8 p-6">
      <SectionHeader icon={Lock} title="Security Settings"
        subtitle="Change your admin password. The new password persists in production." color="text-slate-400" />

      {/* Last login + source */}
      {secInfo && (
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <ClockIcon className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-gray-500">Last login: </span>
            <span className="text-gray-300">
              {secInfo.last_login
                ? new Date(secInfo.last_login).toLocaleString([], { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })
                : "Never recorded"}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <KeyRound className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-gray-500">Password source: </span>
            <span className={secInfo.has_custom_password ? "text-[#00FF41]" : "text-amber-400"}>
              {secInfo.has_custom_password ? "Database (custom)" : "Environment variable"}
            </span>
          </div>
        </div>
      )}

      {/* Change password form */}
      <div className="space-y-3">
        {/* Current password */}
        <div className="relative">
          <input data-testid="current-password" type={showCur ? "text" : "password"}
            value={current} onChange={e => setCurrent(e.target.value)}
            placeholder="Current password" className={inputCls} style={inputStyle}
            onFocus={e => (e.target.style.borderColor = "rgba(255,255,255,0.2)")}
            onBlur={e => (e.target.style.borderColor = "rgba(255,255,255,0.1)")} />
          <button type="button" onClick={() => setShowCur(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors">
            {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* New password */}
          <div className="relative">
            <input data-testid="new-password" type={showNew ? "text" : "password"}
              value={newPwd} onChange={e => setNewPwd(e.target.value)}
              placeholder="New password (min 8 chars)" className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = "rgba(255,255,255,0.2)")}
              onBlur={e => (e.target.style.borderColor = "rgba(255,255,255,0.1)")} />
            <button type="button" onClick={() => setShowNew(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors">
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {/* Confirm */}
          <input data-testid="confirm-password" type="password"
            value={confirm} onChange={e => setConfirm(e.target.value)}
            placeholder="Confirm new password" className={inputCls} style={{
              ...inputStyle,
              borderColor: confirm && newPwd && confirm !== newPwd ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.1)",
            }}
            onFocus={e => (e.target.style.borderColor = "rgba(255,255,255,0.2)")}
            onBlur={e => (e.target.style.borderColor = confirm !== newPwd ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.1)")} />
        </div>

        {/* Validation hints */}
        {newPwd.length > 0 && newPwd.length < 8 && (
          <p className="text-xs text-amber-400">Password must be at least 8 characters</p>
        )}
        {confirm && newPwd !== confirm && (
          <p className="text-xs text-red-400">Passwords don't match</p>
        )}

        {/* Status */}
        <AnimatePresence>
          {status === "success" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
              style={{ background: "rgba(0,255,65,0.08)", border: "1px solid rgba(0,255,65,0.25)", color: "#00FF41" }}>
              <CheckCircle className="w-4 h-4" />
              Password updated. Use your new password on next login.
            </motion.div>
          )}
          {status === "error" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}>
              <AlertCircle className="w-4 h-4" />
              {errMsg}
            </motion.div>
          )}
        </AnimatePresence>

        <button data-testid="change-password-btn" onClick={handleChangePassword}
          disabled={saving || !pwdValid}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{
            background: pwdValid ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
            border: `1px solid ${pwdValid ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.05)"}`,
            color: pwdValid ? "#fff" : "#4b5563",
            cursor: pwdValid && !saving ? "pointer" : "not-allowed",
          }}>
          {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Updating...</> : <><KeyRound className="w-4 h-4" /> Update Password</>}
        </button>
      </div>
    </div>
  );
}

// ── One-Click Migration Panel ─────────────────────────────────────

function MigrationPanel({ token, restaurantId }) {
  const [status, setStatus] = useState(null); // null | "running" | "done" | "error"
  const [result, setResult] = useState(null);

  const runMigration = async () => {
    setStatus("running");
    setResult(null);
    try {
      const res = await fetch(
        `${API_URL}/api/config/admin/migrate-tenant?restaurant_id=${restaurantId}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (res.ok) {
        setStatus("done");
        setResult(data.migrated);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/8 p-6">
      <SectionHeader
        icon={Database}
        title="Migrate Legacy Data"
        subtitle={`Stamps restaurant_id="${restaurantId}" onto all existing SOPs, chat sessions, and receptionist sessions that pre-date multi-tenant isolation. Safe to run multiple times.`}
        color="#8b5cf6"
      />

      <div className="flex items-center gap-4">
        <button
          data-testid="migrate-tenant-btn"
          onClick={runMigration}
          disabled={status === "running"}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{
            background: status === "done"
              ? "rgba(0,255,65,0.1)"
              : "rgba(139,92,246,0.12)",
            border: status === "done"
              ? "1px solid rgba(0,255,65,0.3)"
              : "1px solid rgba(139,92,246,0.3)",
            color: status === "done" ? "#00FF41" : "#a78bfa",
            cursor: status === "running" ? "not-allowed" : "pointer",
          }}
        >
          {status === "running" ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Migrating...</>
          ) : status === "done" ? (
            <><CheckCircle className="w-4 h-4" /> Migration Complete</>
          ) : status === "error" ? (
            <><AlertCircle className="w-4 h-4 text-red-400" /> Retry Migration</>
          ) : (
            <><Database className="w-4 h-4" /> Run Migration</>
          )}
        </button>

        {status === "done" && result && (
          <div className="flex gap-4 text-xs text-gray-500">
            <span><span className="text-white font-mono">{result.sops}</span> SOPs</span>
            <span><span className="text-white font-mono">{result.chat_sessions}</span> chat sessions</span>
            <span><span className="text-white font-mono">{result.receptionist_sessions}</span> receptionist sessions</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ConfigTab ──────────────────────────────────────────────────

export default function ConfigTab({ token, onFoundingChange }) {
  const [config, setConfig] = useState(null);
  const [localConfig, setLocalConfig] = useState(null);
  const [previewPrompt, setPreviewPrompt] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // "success" | "error"
  const [loading, setLoading] = useState(true);

  // ── Fetch config ──────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/config/${RESTAURANT_ID}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setConfig(data);
        setLocalConfig(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  // ── Debounced Echo preview ─────────────────────────────────────────
  const fetchPreview = useCallback(
    debounce(async (cfg) => {
      if (!token || !cfg) return;
      setPreviewLoading(true);
      try {
        // PATCH first so the preview reflects current slider state
        const patchRes = await fetch(`${API_URL}/api/config/${RESTAURANT_ID}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            identity_persona: cfg.identity_persona,
            vapi_config: cfg.vapi_config,
            communication_prefs: cfg.communication_prefs,
            restaurant_profile: cfg.restaurant_profile,
          }),
        });
        if (patchRes.ok) {
          const previewRes = await fetch(
            `${API_URL}/api/config/${RESTAURANT_ID}/echo-prompt`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const previewData = await previewRes.json();
          setPreviewPrompt(previewData.system_prompt || "");
        }
      } catch (e) {
        console.error("Preview fetch failed", e);
      } finally {
        setPreviewLoading(false);
      }
    }, 600),
    [token]
  );

  // Trigger preview on local config change
  useEffect(() => {
    if (localConfig) fetchPreview(localConfig);
  }, [
    localConfig?.identity_persona?.empathy_score,
    localConfig?.identity_persona?.authority_level,
    localConfig?.identity_persona?.voice_tone,
    localConfig?.vapi_config?.interruption_sensitivity,
    localConfig?.communication_prefs?.primary_language,
    localConfig?.restaurant_profile?.name,
  ]);

  // ── Helpers ───────────────────────────────────────────────────────
  const update = (path, value) => {
    setLocalConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    // Propagate founding member mode change to parent immediately
    if (path === "founding_member_mode" && onFoundingChange) {
      onFoundingChange(value);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/config/${RESTAURANT_ID}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(localConfig),
      });
      if (res.ok) {
        setConfig(localConfig);
        setSaveStatus("success");
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus("error");
      }
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !localConfig) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const persona = localConfig.identity_persona || {};
  const vapi = localConfig.vapi_config || {};
  const prefs = localConfig.communication_prefs || {};
  const profile = localConfig.restaurant_profile || {};
  const access = prefs.accessibility || {};
  const isDirty = JSON.stringify(localConfig) !== JSON.stringify(config);

  return (
    <div data-testid="config-tab" className="space-y-6 max-w-4xl">

      {/* ── Founding Member Toggle ──────────────────────────────── */}
      <div
        className="rounded-2xl p-6 transition-all duration-500"
        style={{
          background: localConfig.founding_member_mode
            ? "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))"
            : "rgba(255,255,255,0.02)",
          border: localConfig.founding_member_mode
            ? "1px solid rgba(212,175,55,0.5)"
            : "1px solid rgba(255,255,255,0.08)",
          boxShadow: localConfig.founding_member_mode
            ? "0 0 30px rgba(212,175,55,0.1), inset 0 0 30px rgba(212,175,55,0.03)"
            : "none",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className="p-3 rounded-xl transition-all duration-500"
              style={{
                background: localConfig.founding_member_mode
                  ? "linear-gradient(135deg, #D4AF37, #b8941f)"
                  : "rgba(255,255,255,0.05)",
                boxShadow: localConfig.founding_member_mode
                  ? "0 4px 20px rgba(212,175,55,0.4)"
                  : "none",
              }}
            >
              <Star
                className={`w-6 h-6 transition-all duration-300 ${
                  localConfig.founding_member_mode ? "text-white fill-white" : "text-gray-500"
                }`}
              />
            </div>
            <div>
              <h3 className="font-bold text-white">Founding Member Status</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {localConfig.founding_member_mode
                  ? "Active — Lifetime rate locked at $374/mo. Gold badge displayed in your Command Centre."
                  : "Toggle on to activate your Founding Member perks and badge."}
              </p>
            </div>
          </div>
          <Toggle
            label=""
            checked={localConfig.founding_member_mode}
            onChange={(v) => update("founding_member_mode", v)}
            accent="#D4AF37"
          />
        </div>

        {localConfig.founding_member_mode && (
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { label: "$374/mo", sub: "Locked forever" },
              { label: "First month", sub: "Free" },
              { label: "Direct access", sub: "Founders' WhatsApp" },
            ].map(({ label, sub }) => (
              <div
                key={label}
                className="text-center py-3 rounded-xl"
                style={{
                  background: "rgba(212,175,55,0.07)",
                  border: "1px solid rgba(212,175,55,0.2)",
                }}
              >
                <p className="text-sm font-bold text-[#D4AF37]">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── AI Persona ─────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white/[0.02] border border-white/8 p-6">
        <SectionHeader
          icon={Sliders}
          title="Echo Persona"
          subtitle="Tune Echo's personality. Preview updates live."
          color="#00FF41"
        />

        <div className="grid grid-cols-1 gap-2 mb-4">
          <label className="text-xs text-gray-500 uppercase tracking-wider">Voice Tone</label>
          <div className="grid grid-cols-4 gap-2">
            {["warm", "professional", "casual", "authoritative"].map((tone) => (
              <button
                key={tone}
                data-testid={`tone-${tone}`}
                onClick={() => update("identity_persona.voice_tone", tone)}
                className="py-2 px-3 rounded-lg text-xs font-medium capitalize transition-all"
                style={{
                  background: persona.voice_tone === tone
                    ? "rgba(0,255,65,0.12)"
                    : "rgba(255,255,255,0.04)",
                  border: persona.voice_tone === tone
                    ? "1px solid rgba(0,255,65,0.4)"
                    : "1px solid rgba(255,255,255,0.08)",
                  color: persona.voice_tone === tone ? "#00FF41" : "#9ca3af",
                }}
              >
                {tone}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6 mt-6">
          <PersonaSlider
            label="Empathy Score"
            description="How deeply Echo acknowledges emotions before problem-solving"
            value={persona.empathy_score ?? 9}
            min={1} max={10}
            color="#00FF41"
            onChange={(v) => update("identity_persona.empathy_score", v)}
          />
          <PersonaSlider
            label="Authority Level"
            description="How decisive and ownership-taking Echo is when resolving issues"
            value={persona.authority_level ?? 7}
            min={1} max={10}
            color="#3b82f6"
            onChange={(v) => update("identity_persona.authority_level", v)}
          />
          <PersonaSlider
            label="Interruption Sensitivity"
            description="How easily Echo stops speaking when a guest talks. 0.6 = natural human conversation."
            value={vapi.interruption_sensitivity ?? 0.6}
            min={0} max={1} step={0.05}
            format={(v) => {
              if (v >= 0.8) return `${v} — Highly responsive`;
              if (v >= 0.5) return `${v} — Balanced`;
              return `${v} — Authoritative`;
            }}
            color="#D4AF37"
            onChange={(v) => update("vapi_config.interruption_sensitivity", v)}
          />
        </div>

        {/* Echo Preview */}
        <div className="mt-6">
          <EchoPreview prompt={previewPrompt} loading={previewLoading} />
        </div>
      </div>

      {/* ── Restaurant Profile ──────────────────────────────────── */}
      <div className="rounded-2xl bg-white/[0.02] border border-white/8 p-6">
        <SectionHeader
          icon={Shield}
          title="Restaurant Profile"
          subtitle="Echo uses these details when guests ask about hours, location, and cuisine."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: "restaurant_profile.name", label: "Restaurant Name", placeholder: "e.g. La Maison" },
            { key: "restaurant_profile.cuisine_type", label: "Cuisine Type", placeholder: "e.g. Contemporary French" },
            { key: "restaurant_profile.phone", label: "Phone Number", placeholder: "+1 (555) 000-0000" },
            { key: "restaurant_profile.address", label: "Address", placeholder: "123 Main St, City" },
            { key: "identity_persona.agent_name", label: "AI Agent Name", placeholder: "Echo" },
            { key: "vapi_config.phone_number", label: "Vapi Phone Number", placeholder: "+1 (778) 907-9914" },
          ].map(({ key, label, placeholder }) => {
            const keys = key.split(".");
            const val = keys.reduce((o, k) => o?.[k], localConfig) ?? "";
            return (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">
                  {label}
                </label>
                <input
                  data-testid={`input-${key.split(".").pop()}`}
                  type="text"
                  value={val}
                  placeholder={placeholder}
                  onChange={(e) => update(key, e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none transition-colors"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "rgba(212,175,55,0.5)")}
                  onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.08)")}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Service Toggles ─────────────────────────────────────── */}
      <div className="rounded-2xl bg-white/[0.02] border border-white/8 p-6">
        <SectionHeader
          icon={Languages}
          title="Language & Accessibility"
          subtitle="Echo will detect and respond in the guest's language automatically when multilingual is on."
          color="#8b5cf6"
        />

        <div className="mb-5">
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">
            Primary Language
          </label>
          <select
            data-testid="select-primary-language"
            value={prefs.primary_language ?? "en"}
            onChange={(e) => update("communication_prefs.primary_language", e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg text-sm text-white focus:outline-none"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {[
              ["en", "English"],
              ["es", "Spanish"],
              ["fr", "French"],
              ["de", "German"],
              ["it", "Italian"],
              ["pt", "Portuguese"],
              ["ar", "Arabic"],
              ["zh", "Chinese"],
              ["ja", "Japanese"],
              ["ko", "Korean"],
            ].map(([code, name]) => (
              <option key={code} value={code} style={{ background: "#040F49" }}>
                {name} ({code})
              </option>
            ))}
          </select>
        </div>

        <Toggle
          label="Multi-lingual Mode"
          description="Echo auto-detects guest language and responds accordingly"
          checked={(prefs.secondary_languages?.length ?? 0) > 0}
          onChange={(v) => update("communication_prefs.secondary_languages", v ? ["es", "fr"] : [])}
          accent="#8b5cf6"
        />
        <Toggle
          label="High-Clarity Voice Mode"
          description="Slower, clearer speech — ideal for elderly or hard-of-hearing guests"
          checked={prefs.high_clarity_voice ?? false}
          onChange={(v) => update("communication_prefs.high_clarity_voice", v)}
          accent="#8b5cf6"
        />
        <Toggle
          label="Screen Reader Friendly"
          description="Optimises Atlas visual output for assistive technologies"
          checked={access.screen_reader_friendly ?? false}
          onChange={(v) => update("communication_prefs.accessibility.screen_reader_friendly", v)}
          accent="#8b5cf6"
        />
        <Toggle
          label="High Contrast UI"
          description="Increases contrast ratios across Atlas and the staff dashboard"
          checked={access.high_contrast_ui ?? false}
          onChange={(v) => update("communication_prefs.accessibility.high_contrast_ui", v)}
          accent="#8b5cf6"
        />
        <Toggle
          label="Sign Language Ready"
          description="Flags this restaurant for the visual-interface Atlas build (Phase 3)"
          checked={access.sign_language_ready ?? false}
          onChange={(v) => update("communication_prefs.accessibility.sign_language_ready", v)}
          accent="#8b5cf6"
        />
      </div>

      {/* ── Maintenance Mode ─────────────────────────────────── */}
      <div
        className="rounded-2xl p-6 transition-all duration-300"
        style={{
          background: localConfig.is_maintenance_mode
            ? "rgba(239,68,68,0.04)"
            : "rgba(255,255,255,0.02)",
          border: localConfig.is_maintenance_mode
            ? "1px solid rgba(239,68,68,0.25)"
            : "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg transition-all duration-300"
              style={{
                background: localConfig.is_maintenance_mode
                  ? "rgba(239,68,68,0.12)"
                  : "rgba(255,255,255,0.05)",
              }}
            >
              <Power
                className="w-5 h-5 transition-colors duration-300"
                style={{ color: localConfig.is_maintenance_mode ? "#ef4444" : "#6b7280" }}
              />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">Maintenance Mode</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                When ON — AngusXenia.com shows the Coming Soon page.
                Your Emergent preview URL stays fully accessible.
              </p>
            </div>
          </div>
          <Toggle
            label=""
            checked={localConfig.is_maintenance_mode ?? false}
            onChange={(v) => update("is_maintenance_mode", v)}
            accent="#ef4444"
          />
        </div>

        {localConfig.is_maintenance_mode && (
          <div
            className="flex items-start gap-3 p-3 rounded-xl"
            style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}
          >
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-red-300 font-medium">Active — Production domain is showing Coming Soon</p>
              <p className="text-xs text-gray-500 mt-1">
                Changes take effect within 30 seconds. Emergency contact info from your Restaurant Profile will be shown on the Coming Soon page.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── One-Click Data Migration ──────────────────────────── */}
      <MigrationPanel token={token} restaurantId={RESTAURANT_ID} />

      {/* ── Security Settings ────────────────────────────────── */}
      <SecurityPanel token={token} />

      {/* ── Founding Welcome Suite ──────────────────────────── */}
      <FoundingWelcomePanel token={token} config={localConfig} />

      {/* ── Save Bar ─────────────────────────────────────────────── */}
      <div
        className="sticky bottom-0 flex items-center justify-between p-4 rounded-2xl backdrop-blur-md transition-all duration-300"
        style={{
          background: "rgba(4,15,73,0.9)",
          border: isDirty ? "1px solid rgba(212,175,55,0.4)" : "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center gap-3">
          {saveStatus === "success" && (
            <div className="flex items-center gap-2 text-[#00FF41] text-sm">
              <CheckCircle className="w-4 h-4" />
              Config saved
            </div>
          )}
          {saveStatus === "error" && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              Save failed — try again
            </div>
          )}
          {!saveStatus && isDirty && (
            <p className="text-xs text-[#D4AF37]">Unsaved changes</p>
          )}
          {!saveStatus && !isDirty && (
            <p className="text-xs text-gray-600">All changes saved</p>
          )}
        </div>
        <button
          data-testid="save-config-btn"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all"
          style={{
            background: isDirty
              ? "linear-gradient(135deg, #D4AF37, #b8941f)"
              : "rgba(255,255,255,0.05)",
            color: isDirty ? "#000" : "#4b5563",
            cursor: isDirty ? "pointer" : "not-allowed",
            boxShadow: isDirty ? "0 4px 20px rgba(212,175,55,0.3)" : "none",
          }}
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Saving..." : "Save Config"}
        </button>
      </div>
    </div>
  );
}
